/**
 * User Preferences Store
 * Manages user preferences with localStorage persistence
 * Similar pattern to ProjectViewPreferencesStore
 */

import { create } from 'zustand'
import { createJSONStorage, devtools, persist } from 'zustand/middleware'
import {
  DEFAULT_USER_PREFERENCES
  
  
} from '../lib/types/preferences'
import { changeLanguage } from '../lib/i18n'
import { logger } from '../lib/monitoring/logger'
import { LogCategory } from '../lib/monitoring/types'
import type {UpdateUserPreferencesInput, UserPreferences} from '../lib/types/preferences';
import type { DateFormatType, SupportedLocale, TimeFormatType } from '../lib/i18n/config'

interface UserPreferencesState extends UserPreferences {
  // Actions
  setLanguage: (language: SupportedLocale) => void
  setDateFormat: (dateFormat: DateFormatType) => void
  setTimeFormat: (timeFormat: TimeFormatType) => void
  setTheme: (theme: 'light' | 'dark' | 'system') => void
  setTimezone: (timezone: string) => void
  setWeekStartsOn: (day: 0 | 1 | 2 | 3 | 4 | 5 | 6) => void
  updatePreferences: (preferences: UpdateUserPreferencesInput) => void
  resetPreferences: () => void
}

const useUserPreferencesStore = create<UserPreferencesState>()(
  devtools(
    persist(
      (set) => ({
        // Initial state from defaults
        ...DEFAULT_USER_PREFERENCES,

        // Actions
        setLanguage: (language) => {
          set({ language })
          // Also update i18next
          changeLanguage(language).catch((error) => {
            logger.logError(
              error,
              LogCategory.STATE,
              {
                component: 'UserPreferencesStore',
                action: 'setLanguage',
                language,
              }
            )
          })
        },

        setDateFormat: (dateFormat) => {
          set({ dateFormat })
        },

        setTimeFormat: (timeFormat) => {
          set({ timeFormat })
        },

        setTheme: (theme) => {
          set({ theme })
        },

        setTimezone: (timezone) => {
          set({ timezone })
        },

        setWeekStartsOn: (weekStartsOn) => {
          set({ weekStartsOn })
        },

        updatePreferences: (preferences) => {
          set((state) => ({
            ...state,
            ...preferences,
          }))

          // If language changed, update i18next
          if (preferences.language) {
            changeLanguage(preferences.language).catch((error) => {
              logger.logError(
                error,
                LogCategory.STATE,
                {
                  component: 'UserPreferencesStore',
                  action: 'updatePreferences',
                  language: preferences.language,
                }
              )
            })
          }
        },

        resetPreferences: () => {
          set(DEFAULT_USER_PREFERENCES)
          changeLanguage(DEFAULT_USER_PREFERENCES.language).catch((error) => {
            logger.logError(
              error,
              LogCategory.STATE,
              {
                component: 'UserPreferencesStore',
                action: 'resetPreferences',
              }
            )
          })
        },
      }),
      {
        name: 'moly-user-preferences',
        storage: createJSONStorage(() => localStorage),
      },
    ),
    {
      name: 'UserPreferences',
    },
  ),
)

export default useUserPreferencesStore

/**
 * Selector hooks for specific preferences
 */
export const useLanguage = () => useUserPreferencesStore((state) => state.language)
export const useDateFormat = () => useUserPreferencesStore((state) => state.dateFormat)
export const useTimeFormat = () => useUserPreferencesStore((state) => state.timeFormat)
export const useTheme = () => useUserPreferencesStore((state) => state.theme)
export const useTimezone = () => useUserPreferencesStore((state) => state.timezone)
export const useWeekStartsOn = () => useUserPreferencesStore((state) => state.weekStartsOn)
